import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import MobileTopBar from '../components/MobileTopBar';
import { ArrowUp, Plus, Settings as SettingsIcon, Trash2, Clock, Loader2 } from 'lucide-react';
import CreateInterviewModal from '../components/CreateInterviewModal';
import TemplatesGallery from '../components/TemplatesGallery';
import { interviewsApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const ZARA_AVATAR_URL =
  'https://images.unsplash.com/photo-1499358517822-d8578907a095?auto=format&fit=crop&facepad=3&w=360&h=360&q=80';

function PageHeader({ onCreate, onSettings }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-5 md:px-10 pt-6 md:pt-8 pb-2">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-[22px] md:text-[28px] font-bold text-[#1A1533] tracking-tight">AI Interviewer</h1>
        <button className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E9E3FB] text-[#5B4FE8] text-[12.5px] font-semibold hover:bg-[#DFD6FA] transition-colors">
          <ArrowUp size={13} strokeWidth={2.5} />
          Upgrade your plan
        </button>
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        <button
          onClick={onCreate}
          className="btn-primary flex-1 md:flex-none px-4 md:px-5 py-2.5 rounded-full bg-[#5B4FE8] text-white text-[13.5px] md:text-[14px] font-semibold hover:bg-[#4A3FD9]"
        >
          Create Interview
        </button>
        <button
          onClick={onSettings}
          className="px-4 md:px-5 py-2.5 rounded-full bg-white border border-[#E5DEF9] text-[#1A1533] text-[13.5px] md:text-[14px] font-semibold hover:bg-[#F5F1FE] transition-colors inline-flex items-center gap-2"
        >
          <SettingsIcon size={14} />
          <span className="hidden sm:inline">Settings</span>
        </button>
      </div>
    </div>
  );
}

function ZaraEmptyState({ onCreate }) {
  return (
    <div className="flex flex-col items-center text-center max-w-md mx-auto py-12">
      <div className="w-[130px] h-[130px] md:w-[150px] md:h-[150px] rounded-full overflow-hidden bg-black mb-6 ring-4 ring-white shadow-xl">
        <img src={ZARA_AVATAR_URL} alt="Zara AI Recruiter" className="w-full h-full object-cover" loading="eager" />
      </div>
      <p className="text-[14.5px] md:text-[15px] text-[#3A3453] leading-relaxed">
        Hey, I'm Zara. Human recruiters like yourself are my boss.
        I'm here to interview candidates on your behalf & help you hire the top ones.
      </p>
      <p className="text-[14.5px] md:text-[15px] text-[#3A3453] leading-relaxed mt-4">
        You can track the interview results and manage the saved interviews here
      </p>
      <button
        onClick={onCreate}
        className="btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#7A6FEF] text-white text-[14.5px] md:text-[15px] font-semibold hover:bg-[#5B4FE8] shadow-md"
      >
        <Plus size={18} strokeWidth={2.5} />
        Create a new interview
      </button>
    </div>
  );
}

function InterviewCard({ interview, onDelete }) {
  const created = new Date(interview.created_at).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const statusColors = {
    published: 'bg-[#E8F7EE] text-[#1F8A4E]',
    draft: 'bg-[#FFF4E5] text-[#B7721B]',
    archived: 'bg-[#F1F1F4] text-[#6B6485]',
  };
  return (
    <div className="rounded-2xl bg-white border border-[#ECE6FA] p-5 hover:border-[#C7BFF5] hover:shadow-md transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-bold text-[#1A1533] truncate">{interview.name}</h3>
          <div className="flex items-center gap-3 mt-2 text-[12.5px] text-[#4A4463]">
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {interview.total_duration} min</span>
            <span>•</span>
            <span>{interview.flow?.length ?? 0} modules</span>
            <span>•</span>
            <span>Created {created}</span>
          </div>
          <div className="mt-3">
            <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize ${statusColors[interview.status] || statusColors.draft}`}>
              {interview.status}
            </span>
          </div>
        </div>
        <button
          onClick={() => onDelete(interview.id)}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC]"
          aria-label="Delete interview"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function InterviewList({ interviews, onDelete }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-5 md:px-10 pb-10">
      {interviews.map((iv) => (
        <InterviewCard key={iv.id} interview={iv} onDelete={onDelete} />
      ))}
    </div>
  );
}

export default function AIInterviewer() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [presetFlow, setPresetFlow] = useState(null);
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  const refresh = async () => {
    try {
      const data = await interviewsApi.list();
      setInterviews(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load interviews', description: e?.message || 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    try {
      await interviewsApi.remove(id);
      setInterviews((prev) => prev.filter((i) => i.id !== id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.message || 'Network error' });
    }
  };

  const handleApplyTemplate = (template) => {
    setPresetFlow({
      name: template.name,
      opening_phrase: template.opening_phrase || '',
      flow: template.flow || [],
    });
    setOpen(true);
  };

  const handleModalChange = (next) => {
    setOpen(next);
    if (!next) setPresetFlow(null);
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5FF]">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <MobileTopBar title="AI Interviewer" />
        <PageHeader onCreate={() => { setPresetFlow(null); setOpen(true); }} onSettings={() => navigate('/settings')} />
        <TemplatesGallery onApply={handleApplyTemplate} refreshKey={templatesRefreshKey} />

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="text-[#5B4FE8] animate-spin" size={28} />
          </div>
        ) : interviews.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-5 md:px-6">
            <ZaraEmptyState onCreate={() => { setPresetFlow(null); setOpen(true); }} />
          </div>
        ) : (
          <InterviewList interviews={interviews} onDelete={handleDelete} />
        )}
      </main>
      <CreateInterviewModal
        open={open}
        onOpenChange={handleModalChange}
        onSaved={refresh}
        onTemplateSaved={() => setTemplatesRefreshKey((k) => k + 1)}
        preset={presetFlow}
      />
    </div>
  );
}
